import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, TriangleAlert } from 'lucide-react';
import {
  api,
  type Auditoria,
  type EsfuerzoAccion,
  type PrioridadAccion,
  type RangoResenas,
  type RespuestaAuditoria,
} from '@/lib/api';
import { classNames } from '@/lib/utils';
import { fechaCorta } from '@/components/reviews/shared';

/**
 * La lectura de la IA sobre un alojamiento, encima de sus puntos del plan.
 *
 * El plan dice qué se repite; esto dice qué hacer. Se lo escribe el mismo
 * despliegue de Azure OpenAI que usa Propiedades > Copy, a partir de los temas
 * abiertos del piso y de las frases textuales de los huéspedes.
 *
 * **Se pide a mano y se guarda.** No se genera al abrir la pantalla porque hay
 * sesenta y dos alojamientos con puntos abiertos: serían sesenta y dos llamadas
 * a un razonador cada vez que alguien entra en el plan, minutos de espera y una
 * factura, para un texto que solo cambia cuando llegan reseñas nuevas. Cuando
 * ya está guardada se enseña sola, y si el material ha cambiado desde entonces
 * lo dice y ofrece rehacerla.
 */

const ETIQUETA_PRIORIDAD: Record<PrioridadAccion, string> = {
  alta: 'Prioritario',
  media: 'Importante',
  baja: 'Cuando se pueda',
};

// El rosa y el índigo no están en la tabla de overrides de `index.css`, así que
// llevan su `dark:` a mano; el ámbar y el slate sí están y se dejan como van.
const COLOR_PRIORIDAD: Record<PrioridadAccion, string> = {
  alta: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  media: 'bg-amber-100 text-amber-800',
  baja: 'bg-slate-100 text-slate-700',
};

const ETIQUETA_ESFUERZO: Record<EsfuerzoAccion, string> = {
  rapido: 'Rápido',
  medio: 'Medio',
  obra: 'Obra',
};

const COLOR_ESFUERZO: Record<EsfuerzoAccion, string> = {
  rapido: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  medio: 'bg-slate-100 text-slate-700',
  obra: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
};

function mensaje(e: unknown, porDefecto: string): string {
  return e instanceof Error && e.message ? e.message : porDefecto;
}

function Etiqueta({ clase, children }: { clase: string; children: React.ReactNode }) {
  return <span className={classNames('badge text-[10px]', clase)}>{children}</span>;
}

function Contenido({ a }: { a: Auditoria }) {
  return (
    <>
      {a.diagnostico && (
        <p className="text-[13px] leading-relaxed text-slate-700 dark:text-slate-200">
          {a.diagnostico}
        </p>
      )}

      {a.aviso && (
        <p className="flex gap-1.5 text-[12px] text-amber-700 dark:text-amber-300">
          <TriangleAlert size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
          <span>{a.aviso}</span>
        </p>
      )}

      {a.acciones.length > 0 && (
        // Lista numerada y no viñetas: el orden es la mitad del mensaje —lo que
        // más sube la nota con menos esfuerzo va primero— y una viñeta lo esconde.
        <ol className="space-y-2.5">
          {a.acciones.map((accion, i) => (
            <li key={`${accion.titulo}-${i}`} className="flex gap-2.5">
              <span className="shrink-0 w-5 h-5 mt-0.5 rounded-full bg-sereno-blue/60 dark:bg-white/10 text-[11px] font-semibold flex items-center justify-center tabular-nums">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold leading-snug">{accion.titulo}</p>
                {accion.detalle && (
                  <p className="text-[12px] leading-relaxed text-slate-600 dark:text-slate-300 mt-0.5">
                    {accion.detalle}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-1 mt-1">
                  <Etiqueta clase={COLOR_PRIORIDAD[accion.prioridad]}>
                    {ETIQUETA_PRIORIDAD[accion.prioridad]}
                  </Etiqueta>
                  <Etiqueta clase={COLOR_ESFUERZO[accion.esfuerzo]}>
                    {ETIQUETA_ESFUERZO[accion.esfuerzo]}
                  </Etiqueta>
                  {accion.evidencia && (
                    <span className="text-[11px] text-slate-500">{accion.evidencia}</span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {a.puntosFuertes.length > 0 && (
        <p className="text-[12px] text-slate-500">
          <span className="font-medium text-emerald-700 dark:text-emerald-300">No tocar: </span>
          {a.puntosFuertes.join(' · ')}
        </p>
      )}
    </>
  );
}

export default function AuditoriaListing({
  listingId,
  nombre,
  rango,
}: {
  listingId: string;
  nombre: string;
  rango: RangoResenas;
}) {
  const [datos, setDatos] = useState<RespuestaAuditoria | null>(null);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setCargando(true);
    api
      .getAuditoria(listingId, { rango })
      .then((r) => {
        if (vigente) setDatos(r);
      })
      // Que no haya auditoría guardada no es un fallo que merezca un aviso rojo:
      // la mayoría de los pisos no la tendrán nunca.
      .catch(() => {
        if (vigente) setDatos({ auditoria: null });
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });
    return () => {
      vigente = false;
    };
  }, [listingId, rango]);

  const generar = useCallback(() => {
    setGenerando(true);
    setError(null);
    api
      .generarAuditoria(listingId, { rango })
      .then(setDatos)
      .catch((e) => setError(mensaje(e, 'No se pudo generar la auditoría')))
      .finally(() => setGenerando(false));
  }, [listingId, rango]);

  // Mientras se comprueba si hay una guardada no se pinta nada: un esqueleto
  // por cada alojamiento del plan haría parpadear la lista entera al cargar.
  if (cargando) return null;

  const a = datos?.auditoria ?? null;

  if (!a && !generando && !error) {
    return (
      <button
        type="button"
        onClick={generar}
        className="w-full min-h-11 mb-2 px-3 py-2 rounded-xl border border-dashed border-slate-300 dark:border-white/15 text-left text-[13px] text-slate-500 hover:bg-slate-50 dark:hover:bg-white/5 flex items-center gap-2"
      >
        <Sparkles size={15} className="shrink-0 text-sereno-dark/50" aria-hidden="true" />
        <span>
          Pedir a la IA que lea las reseñas de {nombre} y diga qué hacer
        </span>
      </button>
    );
  }

  return (
    <div className="mb-2 rounded-xl border border-sereno-blue dark:border-white/10 bg-sereno-blue/20 dark:bg-white/[0.03] p-3 space-y-2.5">
      <div className="flex items-center gap-1.5">
        <Sparkles size={15} className="shrink-0 text-sereno-dark/60" aria-hidden="true" />
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-sereno-dark/60">
          Qué hacer en esta casa
        </h3>
        {!generando && a && (
          <button
            type="button"
            onClick={generar}
            className="ml-auto shrink-0 inline-flex items-center gap-1 min-h-11 px-2 text-[12px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
          >
            <RefreshCw size={13} aria-hidden="true" />
            Rehacer
          </button>
        )}
      </div>

      {generando && (
        <p className="flex items-center gap-2 text-[13px] text-slate-500">
          <Loader2 className="animate-spin shrink-0" size={15} />
          Leyendo las reseñas y redactando la auditoría. Tarda hasta un minuto.
        </p>
      )}

      {error && (
        <div className="text-[13px] text-amber-700 dark:text-amber-300 space-y-1.5">
          <p>{error}</p>
          <button type="button" onClick={generar} className="btn-secondary min-h-11 py-0 text-sm">
            Reintentar
          </button>
        </div>
      )}

      {!generando && a && <Contenido a={a} />}

      {!generando && a && datos?.generadaEn && (
        <p className="text-[11px] text-slate-500 pt-1 border-t border-sereno-dark/10 dark:border-white/10">
          {datos.desactualizada ? (
            // El aviso va aquí y no arriba a propósito: la auditoría vieja sigue
            // siendo útil, y una alarma sobre el título haría dudar de todo lo
            // que hay debajo cuando lo que ha pasado es que llegó una reseña.
            <span className="text-amber-700 dark:text-amber-300">
              Escrita el {fechaCorta(datos.generadaEn)} con {datos.basadaEn?.menciones ?? 0} menciones;
              ahora hay {datos.ahora?.menciones ?? 0}. Conviene rehacerla.
            </span>
          ) : (
            <>
              Escrita el {fechaCorta(datos.generadaEn)} con {datos.basadaEn?.menciones ?? 0} menciones
              de {datos.basadaEn?.temas ?? 0} temas.
            </>
          )}
        </p>
      )}
    </div>
  );
}
